import flatpickr from "flatpickr";
import { Russian } from "flatpickr/dist/l10n/ru.js"
import { English } from "flatpickr/dist/l10n/default.js";
import Choices from "choices.js";
import BSN from "bootstrap.native";
import { getMetaValue } from "helpers";
import { ApplicationController } from "../application_controller";

const TABLE_ID = "deal_hotels_form_table"
const TAB_ID = "deal_services_hotels"

// Selectors constants for better maintainability
const SELECTORS = {
  ROOM_AMOUNT_CENTS: 'input[name*="[room_amount_cents]"]',
  ROOM_AMOUNT_EXTRA: 'input[name*="[room_amount_extra_cents]"]',
  ROOM_AMOUNT_CURRENCY: 'input[name*="[room_amount_currency]"]',
  PRICE_CURRENCY: 'input[name*="[price_currency]"]',
  TOTAL_MAIN: '.total_main_value',
  TOTAL_ADDITIONAL: '.total_additional_value',
  COUNT_INPUT: 'input.count_input',
  REPEAT_COUNT: 'input.repeat_count_input',
  HIDDEN_PRICE: 'input.hidden_price_input',
  HIDDEN_EXTRA_PRICE: 'input.hidden_extra_price_input',
  MANUAL_PRICE_FLAG: 'input[name*="[room_is_manual_price]"]'
};

export default class extends ApplicationController {
  static targets = [
    "main_form",
    "service_tr",
    "choices_supplier",
    "choices_service",
    "add_popover",
    "price_dropdown",
    "daterange_period",
    "filter_cities"
  ]

  // Helper method to parse numeric values consistently
  parseNumericValue(value) {
    if (!value) return 0;
    const cleaned = value.toString().replace(',', '.').replace(/[^\d.-]/g, '');
    return parseFloat(cleaned) || 0;
  }

  // Update tooltip for manual prices
  updateTooltipForManualPrice(serviceRow) {
    const hotel_price_modal = serviceRow.querySelector('.hotel_price_modal');
    if (!hotel_price_modal) return;

    const tooltipElement = hotel_price_modal.querySelector('[data-controller*="layout--tooltip"]');
    const manualTooltipText = hotel_price_modal.dataset.manualPriceTooltip;

    if (manualTooltipText && tooltipElement) {
      tooltipElement.setAttribute('data-title', manualTooltipText);
    }
  }

  // Common method to update room_amount fields for manual prices
  updateManualPriceAmounts(serviceRow, manual_value, extra_price_value, currency) {
    const count_input = serviceRow.querySelector(SELECTORS.COUNT_INPUT);
    const repeat_count_input = serviceRow.querySelector(SELECTORS.REPEAT_COUNT);
    const count = this.parseNumericValue(count_input?.value) || 1;
    const repeat_count = this.parseNumericValue(repeat_count_input?.value) || 1;

    // Update room_amount fields
    const room_amount_cents = serviceRow.querySelector(SELECTORS.ROOM_AMOUNT_CENTS);
    const room_amount_extra = serviceRow.querySelector(SELECTORS.ROOM_AMOUNT_EXTRA);
    const room_amount_currency = serviceRow.querySelector(SELECTORS.ROOM_AMOUNT_CURRENCY);

    if (room_amount_cents) {
      const base_total_cents = Math.round(manual_value * count * repeat_count * 100);
      room_amount_cents.value = base_total_cents;
    }
    if (room_amount_extra) {
      const extra_total_cents = Math.round(extra_price_value * count * repeat_count * 100);
      room_amount_extra.value = extra_total_cents;
    }
    if (room_amount_currency) {
      room_amount_currency.value = currency;
    }

    return { count, repeat_count };
  }

  connect() {
    if (this.hasChoices_supplierTarget) this.initSupplierChoices();
    if (this.hasChoices_serviceTarget) this.initServiceChoices();
    if (this.hasMain_formTarget) this.setFormTotalHtml();
    if (this.hasDaterange_periodTarget) {
      //
      // set locale
      //
      let app_locale = document.querySelector('body').dataset.lang;
      let locales = {
        "ru": {
          locales: Russian,
          date_format: "d-m-Y",
          datetime_format: "d-m-Y H:i"
        },
        "en": {
          locales: English,
          date_format: "Y-m-d",
          datetime_format: "Y-m-d H:i"
        }
      }

      //
      // locale Options
      //
      const locale_options = locales[app_locale];

      this.daterange_period(this.daterange_periodTarget, locale_options);
    }
  }

  //
  // Actions
  //
  collapseServices(event) {
    event.preventDefault();
    let target = event.currentTarget;
    let collapse = new BSN.Collapse(target)
    if (target.classList.contains("collapsed"))
      collapse.show();
    else collapse.hide();
  }


  filterServiceTable(event) {
    let filters = {}

    if (this.hasFilter_citiesTarget) {
      filters["hotels_city_id"] = this.filter_citiesTarget.choices.getValue(true);
    }

    let url = this.data.get("table-load-url");
    let url_params = new URLSearchParams(filters);

    const content_element = document.getElementById(TAB_ID);

    fetch(url + "?" + url_params, {
      method: 'GET',
      headers: {
        'X-CSRF-Token': document.getElementsByName('csrf-token')[0].getAttribute('content'),
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    }).then((response) => {
      if (response.status == 200) {
        response.text().then((content) => {
          content_element.innerHTML = JSON.parse(content)["table_content"];
        })
      }
    });
  }

  // add selected service
  async addSelectedService(event) {
    event.preventDefault();
    this.removeAddPopover();
    const btn_target = event.currentTarget
    const url = btn_target.getAttribute("data-url")
    if (!btn_target.classList.contains("disabled")) {
      await this.addServiceRequest(url)
      this.enableDisableSaveButtons()
    }
  }

  openModal(event) {
    event.preventDefault();
    let url = event.currentTarget.getAttribute("data-url");
    this.openLayoutModalXL(url);
  }

  statusChange(event) {
    const tr = event.currentTarget.closest("tr");
    this.setFormTotalHtml();
  }

  priceChange(event) {
    const tr = event.currentTarget.closest("tr");
    this.setFormTotalHtml();
  }

  countChange(event) {
    const tr = event.currentTarget.closest("tr");
    // setFormTotalHtml will be called in updateServiceRowWithCalculatedData or recalculateManualPriceTotals after calculation
    this.recalculateServicePrice(tr);
  }

  repeatCountChange(event) {
    const tr = event.currentTarget.closest("tr");
    // setFormTotalHtml will be called in updateServiceRowWithCalculatedData or recalculateManualPriceTotals after calculation
    this.recalculateServicePrice(tr);
  }

  // Handle period date changes
  periodDateChange(event) {
    const tr = event.currentTarget.closest("tr");

    // When dates change, reset to automatic pricing (manual price is no longer valid for new dates)
    const manualPriceInput = tr.querySelector('input[name*="[room_is_manual_price]"]');
    if (manualPriceInput) {
      manualPriceInput.value = false;
    }

    // Calendar controller already updates nights count via days-count-class attribute
    // Just need to recalculate price after calendar updates the nights field
    // setFormTotalHtml will be called in updateServiceRowWithCalculatedData after API response
    this.recalculateServicePrice(tr);
  }

  // Main method to recalculate service price using API
  async recalculateServicePrice(serviceRow) {
    const roomIdInput = serviceRow.querySelector('input[name*="[room_id]"]');
    const startDateInput = serviceRow.querySelector('.form-group input[name*="[start_at]"]');
    const endDateInput = serviceRow.querySelector('.form-group input[name*="[end_at]"]');
    const countInput = serviceRow.querySelector('input[name*="[count]"]');
    const repeatCountInput = serviceRow.querySelector('input[name*="[repeat_count]"]');
    const manualPriceInput = serviceRow.querySelector('input[name*="[room_is_manual_price]"]');
    
    // Skip if manual price is set - preserve manual prices when dates change, but recalculate totals for count changes
    if (manualPriceInput && manualPriceInput.value === 'true') {
      this.recalculateManualPriceTotals(serviceRow);
      return;
    }

    // Skip if required fields are missing
    if (!roomIdInput || !roomIdInput.value || !startDateInput || !endDateInput) {
      return;
    } 

    const formData = {
      room_id: roomIdInput.value,
      start_at: startDateInput.value,
      end_at: endDateInput.value,
      count: countInput ? parseInt(countInput.value) || 1 : 1,
      repeat_count: repeatCountInput ? parseFloat(repeatCountInput.value) || 1.0 : 1.0
    };

    try {
      // Get the deal ID from the URL or form data
      const dealId = window.location.pathname.match(/\/deals\/([^\/]+)/)?.[1];
      const calculateUrl = `/deals/${dealId}/services/hotels/calculate_price`;

      const response = await fetch(calculateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.getElementsByName('csrf-token')[0].getAttribute('content'),
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'same-origin',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const result = await response.json();
        this.updateServiceRowWithCalculatedData(serviceRow, result);
      } else {
        console.error('Price calculation failed:', response.statusText);
      }
    } catch (error) {
      console.error('Price calculation error:', error);
    }
  }

  // Update service row with calculated price data
  updateServiceRowWithCalculatedData(serviceRow, calculatedData) {
    // Update hidden amount fields
    const amountCentsInput = serviceRow.querySelector('input[name*="[room_amount_cents]"]');
    const amountExtraCentsInput = serviceRow.querySelector('input[name*="[room_amount_extra_cents]"]');
    const amountCurrencyInput = serviceRow.querySelector('input[name*="[room_amount_currency]"]');
    const roomsMetadataInput = serviceRow.querySelector('input[name*="[rooms_metadata]"]');
    const requireRecalculationInput = serviceRow.querySelector('input[name*="[_require_recalculation]"]');

    if (amountCentsInput) amountCentsInput.value = calculatedData.room_amount_cents;
    if (amountExtraCentsInput) amountExtraCentsInput.value = calculatedData.room_amount_extra_cents;
    if (amountCurrencyInput) amountCurrencyInput.value = calculatedData.room_amount_currency;
    if (roomsMetadataInput) roomsMetadataInput.value = JSON.stringify(calculatedData.rooms_metadata);
    requireRecalculationInput.value = 'true';

    // Update displayed totals
    const totalMainValue = serviceRow.querySelector('.total_main_value');
    const totalAdditionalValue = serviceRow.querySelector('.total_additional_value');

    if (totalMainValue) totalMainValue.textContent = calculatedData.total_main_formatted;
    if (totalAdditionalValue) totalAdditionalValue.textContent = calculatedData.total_additional_formatted;

    // Update tooltip if present
    const tooltipElement = serviceRow.querySelector('[data-controller="layout--tooltip"]');
    if (tooltipElement && calculatedData.tooltip_html) {
      // Update tooltip with ready HTML from backend
      tooltipElement.setAttribute('data-title', calculatedData.tooltip_html);

      // If tooltip is currently visible, update its content
      const tooltipContent = document.querySelector('.tooltip-content');
      if (tooltipContent) {
        tooltipContent.innerHTML = calculatedData.tooltip_html;
      }
    }

    // Update form total after individual row totals are updated
    this.setFormTotalHtml();
  }

  // Recalculate totals for manual prices when count/repeat_count changes
  recalculateManualPriceTotals(serviceRow) {
    const hidden_price_target = serviceRow.querySelector(SELECTORS.HIDDEN_PRICE);
    const hidden_extra_price_target = serviceRow.querySelector(SELECTORS.HIDDEN_EXTRA_PRICE);

    if (!hidden_price_target || !hidden_extra_price_target) {
      return;
    }

    const manual_value = this.parseNumericValue(hidden_price_target.value);
    const extra_price_value = this.parseNumericValue(hidden_extra_price_target.value);

    // Get currency from price_currency input
    const price_currency_input = serviceRow.querySelector(SELECTORS.PRICE_CURRENCY);
    const currency = price_currency_input ? price_currency_input.value : 'USD';

    // Use common method to update room_amount fields
    const { count, repeat_count } = this.updateManualPriceAmounts(serviceRow, manual_value, extra_price_value, currency);

    // Calculate and display totals
    const main_rate = parseFloat(serviceRow.getAttribute('data-service-currency-to-main-rate'));
    const additional_rate = parseFloat(serviceRow.getAttribute('data-service-currency-to-additional-rate'));

    const manual_main_total = (manual_value + extra_price_value) * count * repeat_count * main_rate;
    const manual_additional_total = (manual_value + extra_price_value) * count * repeat_count * additional_rate;

    // Update displayed totals
    const total_main_value = serviceRow.querySelector(SELECTORS.TOTAL_MAIN);
    const total_additional_value = serviceRow.querySelector(SELECTORS.TOTAL_ADDITIONAL);

    if (total_main_value) total_main_value.textContent = super.setNumericFormat(manual_main_total);
    if (total_additional_value) total_additional_value.textContent = super.setNumericFormat(manual_additional_total);

    // Update form total after manual totals are updated
    this.setFormTotalHtml();
  }


  removeService(event) {
    event.stopImmediatePropagation();
    if (confirm(event.currentTarget.getAttribute("data-confirm-message"))) {
      const tr = event.currentTarget.closest("tr");
      let id = event.currentTarget.getAttribute("data-id")
      if (id) {
        let destroy_target = document.getElementById(id)
        if (destroy_target) destroy_target.value = true
      }
      tr.remove();
      this.reindexTableRows();
      this.setFormTotalHtml();
      this.enableDisableSaveButtons()
    }
  }

  priceOpen(event) {
    event.preventDefault();
    const price_dropdown_button = event.currentTarget;
    const hotel_price_modal = price_dropdown_button.closest('.hotel_price_modal');

    if (hotel_price_modal.dropdown) {
      hotel_price_modal.dropdown.toggle();
    }
    else {
      // Use standard Bootstrap approach - container as dropdown root
      hotel_price_modal.dropdown = new BSN.Dropdown(hotel_price_modal);
      hotel_price_modal.dropdown.toggle();

      hotel_price_modal.addEventListener('hide.bs.dropdown', function (e) {
        const price_target = hotel_price_modal.querySelector("input.price_input");
        let manual_value = parseFloat(price_target.numeric.rawValue.replace(",", '.').replace(/[^\d.-]/g, ''))
        manual_value = manual_value || 0
        let default_value = parseFloat(price_target.getAttribute("data-default-value").replace(",", '.').replace(/[^\d.-]/g, ''))

        if (manual_value <= 0 || default_value !== manual_value)
          price_target.numeric.set(default_value)
      }, false);
    }
  }

  priceCloseNotSet(event) {
    event.preventDefault();
    const hotel_price_modal = event.currentTarget.closest(".hotel_price_modal");
    if (hotel_price_modal.dropdown) hotel_price_modal.dropdown.dispose();
  }

  priceCloseAndSet(event) {
    event.preventDefault();
    const hotel_price_modal = event.currentTarget.closest(".hotel_price_modal");
    const price_target = hotel_price_modal.querySelector("input.price_input");
    const extra_price_target = hotel_price_modal.querySelector("input.extra_price_input");
    const price_span = hotel_price_modal.querySelector("span.total_main_value");
    const tr_row = event.currentTarget.closest("tr")

    let checked_currency = hotel_price_modal.querySelector("input[data-target='deals--hotels.ticket_tr_currency']:checked");
    let manual_value = this.parseNumericValue(price_target.value)
    let extra_price_value = this.parseNumericValue(extra_price_target.value)

    if (manual_value >= 0) {
      price_target.setAttribute("data-default-value", manual_value)
      const tr = event.currentTarget.closest("tr");

      if (manual_value > 0 || extra_price_value > 0)
        price_span.classList.remove("font-danger")
      else
        price_span.classList.add("font-danger")

      const is_manual_price_target = hotel_price_modal.querySelector("input.is_manual_price_input");
      const hidden_price_target = hotel_price_modal.querySelector("input.hidden_price_input");
      const hidden_extra_price_target = hotel_price_modal.querySelector("input.hidden_extra_price_input");

      if (hotel_price_modal.dropdown) hotel_price_modal.dropdown.dispose();
      if (is_manual_price_target) {
        is_manual_price_target.value = true
        // Save manual prices to hidden fields
        if (hidden_price_target) hidden_price_target.value = manual_value;
        if (hidden_extra_price_target) hidden_extra_price_target.value = extra_price_value;

        tr_row.setAttribute('data-service-currency-to-main-rate', parseFloat(checked_currency.dataset['rateToMain']))
        tr_row.setAttribute('data-service-currency-to-additional-rate', parseFloat(checked_currency.dataset['rateToAdditional']))

        // Use common method to update room_amount fields
        const { count, repeat_count } = this.updateManualPriceAmounts(tr_row, manual_value, extra_price_value, checked_currency.value);

        // Update tooltip for manual price
        this.updateTooltipForManualPrice(tr_row);

        // Calculate and display totals
        const main_rate = parseFloat(checked_currency.dataset['rateToMain']);
        const additional_rate = parseFloat(checked_currency.dataset['rateToAdditional']);

        const manual_main_total = (manual_value + extra_price_value) * count * repeat_count * main_rate;
        const manual_additional_total = (manual_value + extra_price_value) * count * repeat_count * additional_rate;

        // Update displayed totals
        const total_main_value = tr_row.querySelector(SELECTORS.TOTAL_MAIN);
        const total_additional_value = tr_row.querySelector(SELECTORS.TOTAL_ADDITIONAL);

        if (total_main_value) total_main_value.textContent = super.setNumericFormat(manual_main_total);
        if (total_additional_value) total_additional_value.textContent = super.setNumericFormat(manual_additional_total);
      }

      this.setFormTotalHtml();
    }

  }


  priceNotCloseClick(event) {
    event.stopPropagation();
  }

  changeCurrency(event) {
    const modal_body = event.currentTarget.closest(".modal-body");
    const currency = event.currentTarget.getAttribute("value");
    const modal_bodyCurrencyTexts = modal_body.querySelectorAll(".modal_currency_text");
    modal_bodyCurrencyTexts.forEach(el => {
      el.innerHTML = currency;
    });
  }

  reloadServicesPrices(event) {
    event.preventDefault();
    const btn_target = event.currentTarget
    const url = btn_target.getAttribute("data-url")
    const message = btn_target.getAttribute("data-confirm-message")

    if (confirm(message)) {
      this.reloadServiceRequest(url);
    }
  }

  reloadServiceRow(event) {
    event.preventDefault();

    const btn_target = event.currentTarget;
    btn_target.setAttribute("disabled", "disabled");

    const url = btn_target.getAttribute("data-url")
    const tr = event.currentTarget.closest("tr");
    let id = tr.getAttribute("data-service-id")

    const data = new FormData();

    if (id) {
      data.append("id", id)
    }

    tr.querySelectorAll("input").forEach( input => {
      data.append(input.name, input.value);
    })

    tr.querySelectorAll("select").forEach( sel => {
      data.append(sel.name, sel.choices.getValue(true));
    })

    tr.querySelectorAll("textarea").forEach( area => {
      data.append(area.name, area.value);
    })

    this.reloadServiceRowRequest(url, data, tr)
  }



  onPostPutSuccess(event) {
    const [data, status, xhr] = event.detail;
    if (data) {
      // show notify
      window.vNotify.success({ text: data.notification_message, title: data.notification_title });
      // update tab data
      let hotels_content = document.getElementById(TAB_ID)
      if (hotels_content) {
        hotels_content.innerHTML = data.table_content
      }
      // hide form modal
      if (data.save_and_new === true || data.save_and_new === "true") {
        // modal set content
        window.layout_modal_xl.setContent(data.form_content)
      } else {
        window.layout_modal_xl.hide();
      }
    }
  }

  // update deal service form
  onPostPutError(event) {
    const [data, status, xhr] = event.detail;
    // modal set content
    window.layout_modal_xl.setContent(data.form_content)
  }


  //
  // private
  //

  //
  // enable_disable buttons
  //
  enableDisableSaveButtons() {
    const save_and_new_btn = document.getElementById("hotels_save_and_new")
    const save_btn = document.getElementById("hotels_save")
    const empty_form_info = document.getElementById("hotels_empty_form_info")
    const table = document.getElementById(TABLE_ID)
    const rows = table.tBodies[0].querySelectorAll("tr:not(.hidden)")
    if (rows.length > 0) {
      if (save_and_new_btn) save_and_new_btn.removeAttribute("disabled")
      save_btn.removeAttribute("disabled")
      empty_form_info.classList.add("hidden")
    } else {
      if (save_and_new_btn) save_and_new_btn.setAttribute("disabled", "disabled")
      save_btn.setAttribute("disabled", "disabled")
      empty_form_info.classList.remove("hidden")
    }
  }

  //
  // reindex table
  //
  reindexTableRows() {
    const table = document.getElementById(TABLE_ID)
    const rows = table.tBodies[0].querySelectorAll("tr:not(.hidden)")
    for (let i = 0; i < rows.length; i++) {
      let tr = rows[i];
      const index_target = tr.querySelector("span.index")
      index_target.innerHTML = i + 1
    }
  }



  //
  // итого для формы
  //
  setFormTotalHtml() {
    const hotel_form_total_main_target = document.getElementById("hotel_form_total_main_value")
    const hotel_form_total_additional_target = document.getElementById("hotel_form_total_additional_value")
    const totals_hash = this.calculateFormTotal();
    hotel_form_total_main_target.innerHTML = super.setNumericFormat(totals_hash["main"]);
    hotel_form_total_additional_target.innerHTML = super.setNumericFormat(totals_hash["additional"]);
  }

  //
  // расчет итого по строкам таблицы
  //
  calculateFormTotal() {
    const table = document.getElementById(TABLE_ID)
    const rows = table.tBodies[0].rows
    let totals_hash = {
      "main": 0,
      "additional": 0
    }
    for (let i = 0; i < rows.length; i++) {
      let tr = rows[i];
      const status_target = tr.querySelector("select.status_select")

      // не суммируем отмененые
      if (status_target.value != "canceled") {
        let row_totals_hash = this.calculateTrSum(tr);
        totals_hash["main"] += row_totals_hash["total_in_main"]
        totals_hash["additional"] += row_totals_hash["total_in_additional"]
      }
    }
    return totals_hash
  }


  //
  // calculate tr sum amount
  //
  calculateTrSum(tr) {
    // Simply use the already calculated and displayed totals from the "Сумма" column
    const main_element = tr.querySelector(SELECTORS.TOTAL_MAIN);
    const additional_element = tr.querySelector(SELECTORS.TOTAL_ADDITIONAL);

    const main_total = main_element ? this.parseNumericValue(main_element.textContent) : 0;
    const additional_total = additional_element ? this.parseNumericValue(additional_element.textContent) : 0;

    return {
      "total_in_main": main_total,
      "total_in_additional": additional_total,
    }
  }


  // add new service to form
  async addServiceRequest(url) {
    let service_input_id = "deal_services_add_new_service_id"
    let service_target = document.getElementById(service_input_id)
    let show_add_popover = this.showAddPopover
    let enable_disable_save_buttons = this.enableDisableSaveButtons
    if (service_target.value) {
      const data = new FormData();
      data.append("room_id", service_target.value);

      await fetch(url, {
        body: data,
        method: "POST",
        dataType: "text/html",
        credentials: "include",
        headers: {
          "X-CSRF-Token": getMetaValue("csrf-token")
        },
      }).then(function (response) {
        if (response.status == 422) {
          response.text().then(function (text) {
            show_add_popover(text)
            enable_disable_save_buttons()
          });
        } else {
          response.text().then(function (text) {
            const table = document.getElementById(TABLE_ID)
            const rows_count = table.tBodies[0].querySelectorAll("tr:not(.hidden)").length + 1;
            let html = text.replace(/NEW_ROW_INDEX/g, rows_count)
            table.tBodies[0].insertAdjacentHTML("beforeend", html);

            enable_disable_save_buttons()
          });
        }
      })
    }
  }


  async reloadServiceRequest(url) {
    let services_table_id = "deal_hotels_form_tbody"
    let table_target = document.getElementById(services_table_id)

    if (table_target) {
      await fetch(url).then( function(response) {
        return response.text()
      }).then( text => {
        table_target.innerHTML = text;
        this.setFormTotalHtml();
      })

    }
  }

  async reloadServiceRowRequest(url, data, tr) {
    const loader = document.querySelector(".ajax_loader.loader")

    if (tr) {
      loader.classList.remove("hidden");

      await fetch(url, {
        body: data,
        method: "POST",
        dataType: "text/html",
        credentials: "include",
        headers: {
          "X-CSRF-Token": getMetaValue("csrf-token")
        },
      }).then( function(response) {
        return response.text()
      }).then( text => {
        tr.outerHTML = text;

        this.reindexTableRows();
        this.setFormTotalHtml();

        loader.classList.add("hidden");
      })
    }
  }

  removeAddPopover() {
    const addBtnTarget = document.getElementById("deal_services_add_new_service_btn")
    if (addBtnTarget.popover) addBtnTarget.popover.dispose()
  }

  showAddPopover(message) {
    const addBtnTarget = document.getElementById("deal_services_add_new_service_btn")
    if (addBtnTarget.popover) addBtnTarget.popover.dispose()

    addBtnTarget.popover = new BSN.Popover(addBtnTarget, {
      template: '<div class="popover" role="tooltip">'
        + '<div class="arrow"></div>'
        + '<h3 class="popover-header"></h3>'
        + '<div class="popover-body">' + message + '</div>'
        + '</div>',
      placement: 'right', //string
      delay: 100, // integer
      dismissible: true,
    });
    addBtnTarget.popover.show()
  }

  // daterange_period
  daterange_period(field, locale_options) {
    let period = null;
    if (field.dataset.period != null) period = field.dataset.period.split("|")
    let min_date = null;
    let max_date = null;
    if (field.dataset.allowable_range != null) [min_date, max_date] = field.dataset.allowable_range.split("|")
    let set_year_limit = this.setYearLimit

    // let is_set_days_count = false
    let days_count_class = field.dataset.daysCountClass;
    let days_count_closest = field.dataset.daysCountClosest;


    const tr = field.closest("tr");
    let hotel_controller = this.hotelController(tr)


    field.flatpickr = flatpickr(field, {
      mode: "range",
      dateFormat: locale_options["date_format"],
      defaultDate: period,
      minDate: min_date,
      maxDate: max_date,
      onChange: function (selectedDates, dateStr, instance) {
        const form_group = field.closest(".form-group")
        const start_at_field = form_group.querySelector("input.start_at")
        const end_at_field = form_group.querySelector("input.end_at")
        let start_at_value = ""
        let end_at_value = ""
        if (selectedDates.length == 0) {
          start_at_value = ""
          end_at_value = ""
        } else if (selectedDates.length == 1) {
          start_at_value = flatpickr.formatDate(selectedDates[0], "Y-m-d")
          end_at_value = flatpickr.formatDate(selectedDates[0], "Y-m-d")
        } else if (selectedDates.length == 2) {
          start_at_value = flatpickr.formatDate(selectedDates[0], "Y-m-d")
          end_at_value = flatpickr.formatDate(selectedDates[1], "Y-m-d")
        }
        if (start_at_field) start_at_field.value = start_at_value
        if (end_at_field) end_at_field.value = end_at_value

        if (days_count_class && days_count_closest) {
          const start_date = new Date(start_at_value).getTime();
          const end_date = new Date(end_at_value).getTime();
          // 1000 milliseconds * 60 minutes * 60 seconds * 24 hours;
          const days_count = Math.ceil((end_date - start_date) / (1000 * 3600 * 24));

          const closest_target = field.closest(days_count_closest)
          const days_count_field = closest_target.querySelector(days_count_class)
          if (days_count_field) {
            if (days_count_field.numeric) days_count_field.numeric.set(days_count)
            else days_count_field.value = days_count

            hotel_controller.setFormTotalHtml();
          }

        }


      },
      onClose: function (selectedDates, dateStr, instance) {
        if (selectedDates.length == 1) {
          instance.setDate([selectedDates[0], selectedDates[0]], true);
        }
      },
      onOpen: function (selectedDates, dateStr, instance) {
        set_year_limit(instance.currentYearElement)
      }
    });

    let startDate = field.getAttribute("data-start-date")
    let calendarValue = field.getAttribute("value")
    if (startDate != undefined && (calendarValue == '' || calendarValue == undefined )) {
      field.flatpickr.jumpToDate(startDate);
    }
  }


  //
  // Form search choices
  //
  initSupplierChoices() {
    //
    // выбор поставщика, обновляем select для списка услуг
    //
    let remove_add_popover = this.removeAddPopover
    let reload_services = this.getSupplierServicesData
    let reload_suppliers = this.getSuppliersSearchData
    let choice = new Choices(this.choices_supplierTarget, {
      removeItemButton: false,
      removeItems: false,
      searchEnabled: true,
      searchChoices: false,
      placeholder: true,
      loadingText: this.messageText("loadingText"),
      noResultsText: this.messageText("noResultsText"),
      noChoicesText: this.messageText("noChoicesText"),
      itemSelectText: this.messageText("itemSelectText")
    });
    // set as input attribute
    this.choices_supplierTarget.choices = choice;
    // search
    choice.passedElement.element.addEventListener(
      "search",
      function (event) {
        reload_suppliers(this, event.detail.value, false);
      },
      false,
    );

    // change
    let reload_choices_id = this.choices_supplierTarget.getAttribute("data-reload-target-id")
    if (reload_choices_id) {
      choice.passedElement.element.addEventListener(
        "change",
        function (event) {
          remove_add_popover();
          const reload_el = document.getElementById(reload_choices_id)
          if (reload_el) {
            reload_services(this, event.detail.value);
          }
        },
        false,
      );
    }
    this.addValidateTrigger(this.choices_supplierTarget, this.choices_supplierTarget.choices)
  }

  initServiceChoices() {
    let remove_add_popover = this.removeAddPopover
    let choice = new Choices(this.choices_serviceTarget, {
      removeItemButton: false,
      removeItems: false,
      searchEnabled: false,
      shouldSort: false,
      placeholder: true,
      loadingText: this.messageText("loadingText"),
      noResultsText: this.messageText("noResultsText"),
      noChoicesText: this.messageText("noChoicesText"),
      itemSelectText: this.messageText("itemSelectText"),
      callbackOnInit: function () {
        if (this.passedElement.element.getAttribute("data-disable") == "true") {
          this.disable();
        }
      }
    });
    // set as input attribute
    this.choices_serviceTarget.choices = choice

    // change
    let btn_id = this.choices_serviceTarget.getAttribute("data-controll-btn")
    choice.passedElement.element.addEventListener(
      "change",
      function (event) {
        const btn = document.getElementById(btn_id)
        if (btn) {
          remove_add_popover();
          if (event.detail.value) {
            btn.classList.remove("disabled")
          } else {
            btn.classList.add("disabled")
          }
        }
      },
      false,
    );
    this.addValidateTrigger(this.choices_serviceTarget, this.choices_serviceTarget.choices)
  }

  //
  // Search Supplier
  //
  async getSuppliersSearchData(target, q) {
    let url = target.getAttribute("data-search-url");
    let choice = target.choices;
    let query = (q == null ? '' : q)
    url += "&q=" + query
    await fetch(url)
      .then(res => res.json())
      .then((data) => {
        choice.setChoices(data, 'value', 'label', true)
      });
  }


  //
  // Supplier services
  //
  async getSupplierServicesData(target, supplier_id) {
    let reload_field_id = target.getAttribute("data-reload-target-id");
    let services_url = target.getAttribute("data-services-url");
    let reload_field = document.getElementById(reload_field_id);

    if (reload_field) {

      let reload_choice = reload_field.choices;
      reload_choice.clearInput();
      reload_choice.clearStore();
      reload_choice.clearChoices();
      reload_choice.setChoiceByValue(null);

      if (supplier_id) {
        reload_choice.setChoices(async () => {
          try {
            // Get deal dates for filtering rooms with available prices
            const dealForm = document.querySelector('[data-controller="deals--hotels"]');
            let urlParams = new URLSearchParams();
            urlParams.append('supplier_id', supplier_id);

            // Add date parameters if available
            const dealStartDate = dealForm?.dataset.dealStartDate;
            const dealEndDate = dealForm?.dataset.dealEndDate;

            if (dealStartDate) urlParams.append('start_date', dealStartDate);
            if (dealEndDate) urlParams.append('end_date', dealEndDate);

            const items = await fetch(services_url + "?" + urlParams.toString());
            return items.json();
          } catch (err) {
            // console.error(err);
          }
        });
        reload_choice.showDropdown();
      }
    }
  }


  messageText(key) {
    let locale = document.body.getAttribute("data-lang");
    const message_list = {
      "ru": {
        loadingText: "Загрузка...",
        noResultsText: "Ничего не найдено",
        noChoicesText: "Нет вариантов выбора",
        itemSelectText: "Нажмите для выбора"
      },
      "en": {
        loadingText: "Loading...",
        noResultsText: "No results found",
        noChoicesText: "No choices to choose from",
        itemSelectText: "Press to select"
      }
    }
    return message_list[locale][key];
  }

  addValidateTrigger(html_element, choices_object) {
    let form, controller, fieldContainer;
    form = html_element.closest('form')
    if (form != undefined)
      controller = this.formController(form)
    if (controller != undefined)
      fieldContainer = html_element.closest('.form-group');

    if (controller != undefined && fieldContainer != undefined) {
      choices_object.passedElement.element.addEventListener(
        'change',
        function (event) {
          controller.checkChoicesMulti(fieldContainer);
        },
        false,
      );
    }
  }

  formController(form_element) {
    return this.application.getControllerForElementAndIdentifier(form_element, "layout--form")
  }

  hotelController(element) {
    return this.application.getControllerForElementAndIdentifier(element, "deals--hotels")
  }

  // yearLimit
  setYearLimit(year_target) {
    if (year_target) {
      year_target.type = "text"
      year_target.pattern = "\d*"
      year_target.maxLength = 4
      year_target.addEventListener(
        "keydown",
        function (event) {
          const charCode = (event.which) ? event.which : event.keyCode
          if (charCode > 31 && (charCode < 48 || charCode > 57)) {
            event.preventDefault();
            return false;
          }
          return true;
        }
      );
    }
  }

}
